import createClient from "openapi-fetch";
import type { components, paths } from "./schema";

export type Schemas = components["schemas"];
export type Surface = "owner" | "tenant" | "user";

/** Problem details (RFC 9457) returned by the API and the BFF. */
export interface Problem {
  status?: number;
  title?: string;
  detail?: string;
  code?: string;
  errors?: Record<string, string[]>;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly problem: Problem,
  ) {
    super(problem.detail ?? problem.title ?? `Request failed (${status})`);
  }
}

/**
 * Typed client for one surface. Calls go to the same-origin BFF (`/bff/<surface>/api/...`), which attaches the
 * access token server side. The browser never sees a token; the X-CSRF header defeats cross-site requests.
 */
export function createApi(surface: Surface) {
  return createClient<paths>({
    baseUrl: `/bff/${surface}`,
    credentials: "same-origin",
    headers: { "X-CSRF": "1" },
  });
}

let unauthorizedHandler: (() => void) | undefined;

/** Called when an API call answers 401 mid-session (session ended or revoked), e.g. to send the user to sign-in. */
export function onUnauthorized(handler: (() => void) | undefined): void {
  unauthorizedHandler = handler;
}

/** Unwraps an openapi-fetch result: returns data, throws ApiError for problem responses. */
export async function call<T>(request: Promise<{ data?: T; error?: unknown; response: Response }>): Promise<T> {
  const { data, error, response } = await request;
  if (response.status === 401) unauthorizedHandler?.();
  if (!response.ok) {
    throw new ApiError(response.status, (error as Problem | undefined) ?? { status: response.status });
  }
  return data as T;
}

export interface Session {
  surface: Surface;
  email: string | null;
  name: string | null;
  roles: string[];
  tenantId: string | null;
  hasRequiredRole: boolean;
  requiredRole: string;
  logoutUrl: string;
}

/** The signed-in session for the surface, or null when not signed in. */
export async function fetchSession(surface: Surface): Promise<Session | null> {
  const response = await fetch(`/bff/${surface}/session`, { headers: { "X-CSRF": "1" }, credentials: "same-origin" });
  if (response.status === 401) return null;
  if (!response.ok) throw new ApiError(response.status, { status: response.status, detail: "Could not load the session." });
  return (await response.json()) as Session;
}

export function loginUrl(surface: Surface, returnUrl: string): string {
  return `/bff/${surface}/login?returnUrl=${encodeURIComponent(returnUrl)}`;
}

/**
 * Commands never return data, so the caller generates ids: RFC 9562 UUID version 7
 * (48-bit millisecond timestamp, then random bits).
 */
export function uuidv7(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  const ms = BigInt(Date.now());
  for (let i = 0; i < 6; i++) bytes[i] = Number((ms >> BigInt(8 * (5 - i))) & 0xffn);
  bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x70;
  bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}
